// ══════════════════════════════════════════════════════════
// RADIO PAGE UTILS — Mapper compartido
//
// Convierte registros reales de Supabase (Radio) al tipo
// RadioStation que consumen los componentes de la página.
//
// Para la radio de plataforma, inyecta el stream URL HTTPS
// oficial vía streamUrlBuilder (misma lógica que DigitalDial).
// ══════════════════════════════════════════════════════════

import type { Radio } from '@/types/radios';
import type { RadioStation } from '@/mocks/radios';
import { buildStreamUrl } from '@/services/streamUrlBuilder';

/* ── Parámetros del stream oficial de RadioFrecuenciaWeb ── */
export const RFW_STREAM_PARAMS = {
  loginHostname: 'cast3.asurahosting.com',
  streamUsername: 'radiofrecuenciaweb',
  mountpoint: 'stream',
  streamingType: 'shoutcast' as const,
};

/* ── Stream URL HTTPS oficial ── */
const PLATFORM_STREAM_URL = buildStreamUrl(RFW_STREAM_PARAMS);
// Resultado: https://cast3.asurahosting.com/proxy/radiofrecuenciaweb/stream

/**
 * Convierte un registro real de Supabase (Radio) al tipo RadioStation
 * usado por los componentes de la página pública /radio/:slug.
 *
 * RadioFrecuenciaWeb (is_platform_radio = true):
 * - Usa el stream URL HTTPS oficial del proxy AsuraHosting.
 * - NO usa stream_url de Supabase (puede ser HTTP obsoleto).
 *
 * Radios normales:
 * - Valores neutros para campos que no existen en Supabase.
 */
export function mapRadioToStation(radio: Radio): RadioStation {
  const isOfficial = radio.is_platform_radio;
  const secureStreamUrl = isOfficial ? PLATFORM_STREAM_URL : '';

  return {
    id: 0,
    name: radio.name,
    slug: radio.slug,
    frequency: isOfficial ? 'RFW Oficial' : (radio.frequency || ''),
    city: radio.city || '—',
    state: radio.country || '—',
    country: radio.country || '—',
    category: isOfficial ? 'Oficial' : (radio.genre || 'General'),
    language: radio.language || 'es',
    hostName: isOfficial ? 'RFW Oficial' : '—',
    listeners: 0,
    rating: isOfficial ? 5.0 : 4.0,
    isLive: isOfficial,
    logoUrl: radio.logo_url || '',
    description: radio.description || '',
    radioScore: isOfficial ? 10.0 : 0,
    cabinaMessages: 0,
    recentPosts: 0,
    isPremium: isOfficial,
    isTopRadio: isOfficial,
    cabinaActiva: isOfficial,
    hostingType: isOfficial ? 'rfw_hosting' : 'external',
    streamProvider: isOfficial ? 'centovacast' : 'external',
    providerType: isOfficial ? 'rfw_hosted' : 'custom_url',
    streamUrl: secureStreamUrl,
    streamStatus: 'online',
    technicalStreamStatus: isOfficial ? 'active' : 'active',
    streamAvailable: isOfficial,
    listenerCount: 0,
    currentMetadata: null,
    lastTechnicalSyncAt: null,
    publicStreamUrl: isOfficial ? secureStreamUrl : null,
    publicSecureStreamUrl: null,
    isPlatformRadio: isOfficial,
  };
}